import React from 'react'
import LoadingSpinner from '../../ui/LoadingSpinner'
import MetricCard from '../../ui/MetricCard'
import DataTable from '../../ui/DataTable'
import { getOperatorInfo, MONTH_NAMES, AVAILABLE_YEARS } from './constants'

const KAMPerKamView = ({
  // Filters state
  selectedKamName,
  perKamOperator,
  timeRangeType,
  customStartMonth,
  customStartYear,
  customEndMonth,
  customEndYear,
  operatorOptions,
  uniqueKamNames,
  
  // Data state
  perKamData,
  perKamLoading,
  exportLoading,
  
  // Setters
  onKamNameChange,
  onOperatorChange,
  onTimeRangeTypeChange,
  onCustomStartMonthChange,
  onCustomStartYearChange,
  onCustomEndMonthChange,
  onCustomEndYearChange,
  
  // Actions
  onFetchData,
  onExport
}) => {
  const getPercentageColor = (value) => {
    if (value >= 100) return 'text-green-700 bg-green-50'
    if (value >= 80) return 'text-blue-700 bg-blue-50'
    if (value >= 60) return 'text-orange-700 bg-orange-50'
    return 'text-red-700 bg-red-50'
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">
        <i className="fa-solid fa-user mr-2"></i>
        Porównanie Wydajności Indywidualnej
      </h3>
      
      {/* Filters Section */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-4">
        {/* KAM Selector and Operator Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Wybierz KAMa</label>
            <select
              value={selectedKamName}
              onChange={(e) => onKamNameChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Wybierz KAMa --</option>
              {uniqueKamNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Operator</label>
            <select
              value={perKamOperator}
              onChange={(e) => onOperatorChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {operatorOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Time Range Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Zakres Czasu</label>
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { id: 'current', label: 'Bieżący miesiąc' },
              { id: 'previous', label: 'Poprzedni miesiąc' },
              { id: 'ytd', label: 'Od początku roku' },
              { id: 'custom', label: 'Własny zakres' }
            ].map(option => (
              <button
                key={option.id}
                onClick={() => onTimeRangeTypeChange(option.id)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  timeRangeType === option.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          {/* Custom Range Inputs */}
          {timeRangeType === 'custom' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-white rounded border border-gray-300">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start: Miesiąc</label>
                <select
                  value={customStartMonth}
                  onChange={(e) => onCustomStartMonthChange(parseInt(e.target.value))}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md"
                >
                  {MONTH_NAMES.map((name, idx) => (
                    <option key={idx + 1} value={idx + 1}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start: Rok</label>
                <select
                  value={customStartYear}
                  onChange={(e) => onCustomStartYearChange(parseInt(e.target.value))}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md"
                >
                  {AVAILABLE_YEARS.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Koniec: Miesiąc</label>
                <select
                  value={customEndMonth}
                  onChange={(e) => onCustomEndMonthChange(parseInt(e.target.value))}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md"
                >
                  {MONTH_NAMES.map((name, idx) => (
                    <option key={idx + 1} value={idx + 1}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Koniec: Rok</label>
                <select
                  value={customEndYear}
                  onChange={(e) => onCustomEndYearChange(parseInt(e.target.value))}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md"
                >
                  {AVAILABLE_YEARS.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
        
        {/* Fetch Data Button */}
        <div className="flex gap-2">
          <button
            onClick={onFetchData}
            disabled={!selectedKamName || perKamLoading}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              !selectedKamName || perKamLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {perKamLoading ? 'Ładowanie...' : 'Pokaż Dane'}
          </button>
          {perKamData && perKamData.length > 0 && (
            <button
              onClick={onExport}
              disabled={exportLoading}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 ${
                exportLoading
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {exportLoading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  Eksportowanie...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-download"></i>
                  Eksportuj do XLSX
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      {/* Export Loading Overlay */}
      {exportLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl flex flex-col items-center">
            <i className="fa-solid fa-spinner fa-spin text-blue-600 text-4xl mb-4"></i>
            <p className="text-lg font-semibold text-gray-800">Generowanie pliku XLSX...</p>
            <p className="text-sm text-gray-500 mt-2">To może potrwać kilka minut dla dłuższych okresów</p>
          </div>
        </div>
      )}
      
      {/* Loading State */}
      {perKamLoading && (
        <LoadingSpinner message="Ładowanie danych wydajności KAMa..." />
      )}
      
      {/* No KAM Selected */}
      {!selectedKamName && !perKamLoading && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <i className="fa-solid fa-users text-gray-400 text-4xl mb-4"></i>
          <p className="text-gray-600 text-lg">Wybierz KAMa aby zobaczyć jego wydajność</p>
          <p className="text-gray-500 text-sm mt-2">Możesz również wybrać okres czasu i operatora</p>
        </div>
      )}
      
      {/* Data Display */}
      {!perKamLoading && selectedKamName && perKamData && (
        <>
          {perKamData.length === 0 ? (
            <div className="text-center py-12 bg-yellow-50 rounded-lg border border-yellow-200">
              <i className="fa-solid fa-bullseye text-yellow-600 text-4xl mb-4"></i>
              <p className="text-gray-700 text-lg">Brak danych dla wybranych kryteriów</p>
              <p className="text-gray-600 text-sm mt-2">Spróbuj wybrać inny zakres czasu lub operatora</p>
            </div>
          ) : (
            <div className="space-y-6">
              {perKamData.map((kamOperatorData, idx) => {
                const { kam_name, operator_code, monthly_data, summary } = kamOperatorData
                const operatorInfo = getOperatorInfo(operator_code)
                
                return (
                  <div key={idx} className="bg-white rounded-lg shadow p-6 space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <i className="fa-solid fa-user text-blue-600 mr-2"></i>
                        <h4 className="text-lg font-semibold text-gray-900">{kam_name}</h4>
                        <div 
                          className="ml-3 px-3 py-1 rounded-full text-white text-xs font-medium"
                          style={{ backgroundColor: operatorInfo.color }}
                        >
                          {operatorInfo.display_name}
                        </div>
                      </div>
                    </div>
                    
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <MetricCard
                        title="Łączna Sprzedaż / Cel"
                        value={`${summary.total_sales.toLocaleString()} / ${summary.total_goal.toLocaleString()}`}
                        icon={() => <i className="fa-solid fa-bullseye text-blue-600"></i>}
                      />
                      <MetricCard
                        title="Średnie Osiągnięcie"
                        value={`${summary.avg_achievement.toFixed(1)}%`}
                        icon={() => <i className="fa-solid fa-arrow-trend-up text-green-600"></i>}
                      />
                      <MetricCard
                        title="Łączne Wizyty"
                        value={summary.total_visits.toString()}
                        icon={() => <i className="fa-solid fa-location-dot text-purple-600"></i>}
                      />
                      <MetricCard
                        title="Miesiące z Celami"
                        value={`${summary.months_with_goals}`}
                        icon={() => <i className="fa-regular fa-calendar text-orange-600"></i>}
                      />
                    </div>
                    
                    {/* Monthly Breakdown Table */}
                    <div>
                      <h5 className="text-md font-semibold text-gray-800 mb-3">
                        <i className="fa-solid fa-chart-bar mr-2"></i>
                        Rozbicie Miesięczne
                      </h5>
                      <DataTable
                        rows={monthly_data.map((m, i) => ({ 
                          id: i,
                          month_year: `${m.month}/${m.year}`,
                          month: m.month,
                          year: m.year,
                          ...m 
                        }))}
                        columns={[
                          {
                            field: 'month_year',
                            headerName: 'Miesiąc',
                            flex: 1,
                            minWidth: 100,
                            type: 'string'
                          },
                          {
                            field: 'goal_value',
                            headerName: 'Cel MIN',
                            flex: 1,
                            minWidth: 90,
                            type: 'number',
                            align: 'right',
                            headerAlign: 'right',
                            renderCell: (params) => (
                              <span className="font-semibold text-blue-900">
                                {params.value.toLocaleString()}
                              </span>
                            )
                          },
                          {
                            field: 'goal_value_target',
                            headerName: 'Cel TARGET',
                            flex: 1,
                            minWidth: 90,
                            type: 'number',
                            align: 'right',
                            headerAlign: 'right',
                            renderCell: (params) => {
                              const val = params.value || params.row.goal_value || 0
                              return <span className="font-semibold text-blue-700">{val.toLocaleString()}</span>
                            }
                          },
                          {
                            field: 'actual_sales_mtd',
                            headerName: 'Realizacja',
                            flex: 1,
                            minWidth: 110,
                            type: 'number',
                            align: 'right',
                            headerAlign: 'right',
                            renderCell: (params) => {
                              const rowMonth = params.row.month
                              const rowYear = params.row.year
                              const now = new Date()
                              const isCurrentMonth = rowMonth === (now.getMonth() + 1) && rowYear === now.getFullYear()
                              return (
                                <span className="font-semibold text-gray-900" title={isCurrentMonth ? 'Month-to-Date' : 'Końcowa realizacja'}>
                                  {params.value.toLocaleString()}
                                  {isCurrentMonth && <span className="text-xs text-gray-500 ml-1">(MTD)</span>}
                                </span>
                              )
                            }
                          },
                          {
                            field: 'achievement_percent',
                            headerName: 'Realizacja %',
                            flex: 1,
                            minWidth: 90,
                            type: 'number',
                            align: 'right',
                            headerAlign: 'right',
                            renderCell: (params) => (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPercentageColor(params.value)}`}>
                                {params.value.toFixed(1)}%
                              </span>
                            )
                          },
                          {
                            field: 'forecast_vs_goal_percent',
                            headerName: 'Prognoza',
                            flex: 1.2,
                            minWidth: 120,
                            type: 'number',
                            align: 'right',
                            headerAlign: 'right',
                            renderCell: (params) => {
                              const value = params.value
                              const rowMonth = params.row.month
                              const rowYear = params.row.year
                              const now = new Date()
                              const isCurrentMonth = rowMonth === (now.getMonth() + 1) && rowYear === now.getFullYear()
                              
                              if (!isCurrentMonth) {
                                return <span className="text-gray-400 text-xs" title="Prognoza dotyczy tylko bieżącego miesiąca">n/d</span>
                              }
                              
                              if (value === null || value === undefined) {
                                return <span className="text-gray-500 text-xs">-</span>
                              }
                              
                              return (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPercentageColor(value)}`} title="Prognoza na koniec miesiąca">
                                  <i className="fa-solid fa-bullseye mr-1"></i>
                                  {value.toFixed(1)}%
                                </span>
                              )
                            }
                          },
                          {
                            field: 'total_visits_month',
                            headerName: 'Wizyty',
                            flex: 1,
                            minWidth: 80,
                            type: 'number',
                            align: 'right',
                            headerAlign: 'right'
                          },
                          {
                            field: 'unique_salons_month',
                            headerName: 'Salony',
                            flex: 1,
                            minWidth: 80,
                            type: 'number',
                            align: 'right',
                            headerAlign: 'right'
                          },
                          {
                            field: 'team_rank',
                            headerName: 'Ranking',
                            flex: 1,
                            minWidth: 90,
                            align: 'center',
                            headerAlign: 'center',
                            renderCell: (params) => {
                              const rank = params.value
                              const total = params.row.team_count
                              if (!rank || !total) {
                                return <span className="text-gray-400 text-xs">-</span>
                              }
                              const isTop = rank <= Math.ceil(total / 3)
                              const isBottom = rank > Math.ceil(total * 2 / 3)
                              return (
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  isTop ? 'bg-green-100 text-green-800' :
                                  isBottom ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {rank}/{total}
                                </span>
                              )
                            }
                          },
                          {
                            field: 'vs_team_avg',
                            headerName: 'vs Zespół',
                            flex: 1,
                            minWidth: 100,
                            type: 'number',
                            align: 'right',
                            headerAlign: 'right',
                            renderCell: (params) => {
                              const diff = params.value
                              const teamAvg = params.row.team_avg_achievement
                              if (diff === null || diff === undefined) {
                                return <span className="text-gray-400 text-xs">-</span>
                              }
                              const isPositive = diff > 0
                              const isNegative = diff < 0
                              return (
                                <div className="flex flex-col items-end">
                                  <span className={`text-xs font-bold ${
                                    isPositive ? 'text-green-600' :
                                    isNegative ? 'text-red-600' :
                                    'text-gray-600'
                                  }`}>
                                    {isPositive ? '+' : ''}{diff.toFixed(1)}%
                                  </span>
                                  <span className="text-[10px] text-gray-400">
                                    śr. {teamAvg?.toFixed(0)}%
                                  </span>
                                </div>
                              )
                            }
                          },
                          {
                            field: 'has_goal',
                            headerName: 'Status',
                            flex: 1,
                            minWidth: 110,
                            renderCell: (params) => {
                              const hasGoal = params.value
                              const achievement = params.row.achievement_percent
                              const rowMonth = params.row.month
                              const rowYear = params.row.year
                              const now = new Date()
                              const isCurrentMonth = rowMonth === (now.getMonth() + 1) && rowYear === now.getFullYear()
                              
                              const getStatusInfo = () => {
                                if (!hasGoal) {
                                  return { text: 'Brak celu', className: 'bg-gray-200 text-gray-600' }
                                }
                                if (achievement >= 100) {
                                  return { text: '✓ Osiągnięty', className: 'bg-green-100 text-green-800' }
                                }
                                if (isCurrentMonth) {
                                  return achievement >= 80 
                                    ? { text: 'W trakcie', className: 'bg-blue-100 text-blue-800' }
                                    : { text: 'W trakcie', className: 'bg-orange-100 text-orange-800' }
                                } else {
                                  return { text: '✗ Nieosiągnięty', className: 'bg-red-100 text-red-800' }
                                }
                              }
                              
                              const { text, className } = getStatusInfo()
                              return (
                                <span className={`px-2 py-1 rounded text-xs font-medium ${className}`}>
                                  {text}
                                </span>
                              )
                            }
                          }
                        ]}
                        pageSize={12}
                        autoHeight
                        sortModel={[{ field: 'year', sort: 'desc' }, { field: 'month', sort: 'desc' }]}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default KAMPerKamView


